export type legal_section = 'terms' | 'privacy';

type legal_url_options = {
  section?: legal_section;
};

/** Matches website `next-intl` routing (`localePrefix: as-needed`, default `fr`). */
export const WEBSITE_DEFAULT_LOCALE = 'fr' as const;
export type WebsiteLocale = 'fr' | 'en';

const WEBSITE_BASE_URL = import.meta.env.VITE_WEBSITE_URL ?? 'https://www.nouspigeons.ca';

/**
 * Path on the marketing site for an article, including locale prefix when not default.
 * Example: `/ressources/articles/my-slug` (fr) or `/en/ressources/articles/my-slug` (en).
 */
export const get_website_article_path = (
  locale: WebsiteLocale,
  slug: string,
  options?: { preview?: boolean }
): string => {
  const qs = options?.preview ? '?preview=1' : '';
  const path = `/ressources/articles/${slug}${qs}`;
  if (locale === WEBSITE_DEFAULT_LOCALE) {
    return path;
  }
  return `/en${path}`;
};

export const get_website_article_public_url = (locale: WebsiteLocale, slug: string): string =>
  `${get_website_base_url()}${get_website_article_path(locale, slug)}`;

export const get_website_article_preview_url = (locale: WebsiteLocale, slug: string): string =>
  `${get_website_base_url()}${get_website_article_path(locale, slug, { preview: true })}`;

/** Path on the marketing site for a campaign landing page, e.g. `/campaigns/montreal-photographers`. */
export const get_campaign_path = (slug: string): string => `/campaigns/${slug}`;

export const get_campaign_public_url = (slug: string): string =>
  `${get_website_base_url()}${get_campaign_path(slug)}`;

export const get_website_base_url = (): string => {
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;

    if (host === 'localhost' || host === '127.0.0.1') {
      return 'http://localhost:3001';
    }
  }

  return WEBSITE_BASE_URL;
};

/** Relative paths (e.g. `/assets/...`) are served by the marketing site; prefix when showing them from the app admin. */
export const resolve_website_asset_url = (url: string | null | undefined): string => {
  const trimmed = (url ?? '').trim();

  if (!trimmed) {
    return '';
  }

  if (trimmed.startsWith('/')) {
    return `${get_website_base_url()}${trimmed}`;
  }

  return trimmed;
};

export const get_terms_and_confidentiality_url = ({ section }: legal_url_options = {}): string => {
  const legal_url = new URL('/conditions', get_website_base_url());

  if (section) {
    legal_url.hash = section;
  }

  return legal_url.toString();
};
